import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCanPerform } from '../../hooks/useCanPerform';
import { ConfigService } from '../../services/config.service';
import { LoadingSpinner } from '../common';
import {
  SystemConfigData,
  UpdateSystemConfigRequest,
  SYSTEM_CONFIG_DEFAULTS,
} from '../../dtos/admin/systemConfig.dto';

const SystemConfig: React.FC = () => {
  const canWriteConfig = useCanPerform('write:config');
  const [config, setConfig] = useState<SystemConfigData | null>(null);
  const [formData, setFormData] = useState<UpdateSystemConfigRequest>(SYSTEM_CONFIG_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isGeneralExpanded, setIsGeneralExpanded] = useState(true);

  useEffect(() => {
    ConfigService.getConfig()
      .then(data => {
        setConfig(data);
        setFormData({
          medications: data.medications,
          priorAuthorization: data.priorAuthorization,
          pocCertifications: data.pocCertifications,
          providerAssignments: data.providerAssignments,
        });
      })
      .catch(err => setError(err.message || 'Failed to load configuration'))
      .finally(() => setLoading(false));
  }, []);

  const handleFieldChange = (
    entity: keyof UpdateSystemConfigRequest,
    field: string,
    value: number
  ) => {
    setFormData(prev => ({
      ...prev,
      [entity]: { ...prev[entity], [field]: value },
    }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await ConfigService.updateConfig(formData);
      setConfig(saved);
      setIsDirty(false);
      setSuccessMessage('Configuration saved.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (config) {
      setFormData({
        medications: config.medications,
        priorAuthorization: config.priorAuthorization,
        pocCertifications: config.pocCertifications,
        providerAssignments: config.providerAssignments,
      });
    } else {
      setFormData(SYSTEM_CONFIG_DEFAULTS);
    }
    setIsDirty(false);
  };

  if (loading) {
    return <LoadingSpinner className="p-6" />;
  }

  const inputCls = `w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500${!canWriteConfig ? ' bg-gray-100 cursor-not-allowed' : ''}`;

  const renderField = (
    entity: keyof UpdateSystemConfigRequest,
    field: string,
    label: string,
    dotColor: string
  ) => (
    <div key={`${entity}-${field}`}>
      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-1">
        <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${dotColor}`} />
        {label}
      </label>
      <input
        type="number"
        min={1}
        value={(formData[entity] as unknown as Record<string, number>)[field]}
        onChange={e => handleFieldChange(entity, field, Number(e.target.value))}
        disabled={!canWriteConfig}
        className={inputCls}
      />
    </div>
  );

  return (
    <div className="p-6">
      <h2 className="md:hidden text-xl font-semibold text-gray-900 mb-1">System Configuration</h2>
      <p className="md:hidden text-gray-500 text-sm mb-6">
        Configure expiration thresholds for clinical entities.
      </p>

      {!canWriteConfig && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          You have read-only access to system configuration.
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {successMessage}
        </div>
      )}

      {/* General section — collapsible */}
      <div className="mb-6">
        <div className="rounded-lg border border-gray-200 shadow-sm bg-gray-50">
          <div
            className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
            onClick={() => setIsGeneralExpanded(!isGeneralExpanded)}
          >
            <h4 className="text-base font-medium text-gray-900">General</h4>
            {isGeneralExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>

          {isGeneralExpanded && (
            <div className="p-4 space-y-6">
              {/* Medications */}
              <div className="mb-6 last:mb-0">
                <h5 className="text-sm font-semibold text-gray-700 mb-3 pb-1 border-b border-gray-200">
                  Medications
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {renderField('medications', 'defaultExpiryDays', 'Default Expiry (days)', 'bg-gray-400')}
                  {renderField('medications', 'warnYellowDays', 'Warn Yellow (days)', 'bg-yellow-400')}
                  {renderField('medications', 'warnOrangeDays', 'Warn Orange (days)', 'bg-orange-500')}
                  {renderField('medications', 'warnRedDays', 'Warn Red (days)', 'bg-red-500')}
                </div>
              </div>

              {/* Prior Authorization */}
              <div className="mb-6 last:mb-0">
                <h5 className="text-sm font-semibold text-gray-700 mb-3 pb-1 border-b border-gray-200">
                  Prior Authorization
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {renderField('priorAuthorization', 'warnYellowDays', 'Warn Yellow (days)', 'bg-yellow-400')}
                  {renderField('priorAuthorization', 'warnOrangeDays', 'Warn Orange (days)', 'bg-orange-500')}
                  {renderField('priorAuthorization', 'warnRedDays', 'Warn Red (days)', 'bg-red-500')}
                </div>
              </div>

              {/* POC Certifications */}
              <div className="mb-6 last:mb-0">
                <h5 className="text-sm font-semibold text-gray-700 mb-3 pb-1 border-b border-gray-200">
                  POC Certifications
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {renderField('pocCertifications', 'warnYellowDays', 'Warn Yellow (days)', 'bg-yellow-400')}
                  {renderField('pocCertifications', 'warnOrangeDays', 'Warn Orange (days)', 'bg-orange-500')}
                  {renderField('pocCertifications', 'warnRedDays', 'Warn Red (days)', 'bg-red-500')}
                </div>
              </div>

              {/* Provider Assignments */}
              <div className="mb-6 last:mb-0">
                <h5 className="text-sm font-semibold text-gray-700 mb-3 pb-1 border-b border-gray-200">
                  Provider Assignments
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {renderField('providerAssignments', 'warnYellowDays', 'Warn Yellow (days)', 'bg-yellow-400')}
                  {renderField('providerAssignments', 'warnOrangeDays', 'Warn Orange (days)', 'bg-orange-500')}
                  {renderField('providerAssignments', 'warnRedDays', 'Warn Red (days)', 'bg-red-500')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action bar — only shown when dirty and not read-only */}
      {isDirty && canWriteConfig && (
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default SystemConfig;
